import { INJECTABLE_SYMBOL, isInjectable } from './Injectable.js';
/**
 * Symbol used to identify interface instances.
 * @internal
 */
const INTERFACE_SYMBOL = Symbol('interface');
/**
 * Creates a new interface with the specified options.
 * Interfaces support hierarchical resolution through container parents and optional defaults.
 *
 * @template T - The type that the interface will resolve to
 * @param options - Configuration options for the interface
 * @returns A new interface instance
 * @__NO_SIDE_EFFECTS__
 */
export function defineInterface(options = {}) {
    return {
        [INJECTABLE_SYMBOL]: INTERFACE_SYMBOL,
        name: options.name,
        resolve(container, caller) {
            while (container) {
                const value = container.get(this);
                if (value === undefined) {
                    if (container.parent) {
                        container = container.parent;
                        continue;
                    }
                    break;
                }
                if (isInjectable(value)) {
                    return value.resolve(container, caller);
                }
                return value;
            }
            if (options.default) {
                if (isInjectable(options.default)) {
                    return options.default.resolve(container, caller);
                }
                return options.default(container, caller);
            }
            throw new Error(`Interface ${this.name ?? ''} was not found`);
        },
        optional,
        default: defaultValue,
    };
}
/**
 * Type guard to check if a value is an Interface instance.
 * @param value - The value to check
 * @returns True if the value is an Interface, false otherwise
 */
export function isInterface(value) {
    if (value == null) {
        return false;
    }
    return value[INJECTABLE_SYMBOL] === INTERFACE_SYMBOL;
}
/**
 * Sets a default value for an interface.
 * The default value will be used when the interface is not found in the container.
 *
 * @template T - The type that the interface resolves to
 * @template D - The type of the default value
 * @param defaultValue - The default value or injectable to use
 * @returns A new injectable that resolves to T | D
 */
function defaultValue(fallbackValue) {
    return {
        [INJECTABLE_SYMBOL]: true,
        name: this.name,
        resolve: (container, caller) => {
            const value = container.get(this);
            if (value === undefined) {
                if (isInjectable(fallbackValue)) {
                    return fallbackValue.resolve(container, caller);
                }
                return fallbackValue;
            }
            if (isInjectable(value)) {
                return value.resolve(container, caller);
            }
            return value;
        },
    };
}
/**
 * Makes an interface optional, allowing it to resolve to undefined if not found.
 * @template T - The type that the interface resolves to
 * @returns A new injectable that resolves to T | undefined
 */
function optional() {
    return this.default(undefined);
}
//# sourceMappingURL=Interface.js.map